/**
 * DocumentStudio — generate, refine, and publish HidroVerse PDFs straight
 * from the Content Manager.
 *
 * Flow:
 *   1. Admin briefs the AI on a PDF (purpose, outline).
 *   2. AI returns a structured JSON document (title + sections).
 *   3. Section editor lets the admin edit / add / delete / re-order rows.
 *   4. Refine bar (instruction → AI rewrites the whole doc).
 *   5. Re-render → fresh PDF; Publish → appears on /insights as a download.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import {
  Plus, FileText, Trash2, Eye, EyeOff, ExternalLink, Download,
  ArrowUp, ArrowDown, RefreshCw, Sparkles,
} from "lucide-react";
import {
  StatusBadge, RefineBar, RevisionDrawer, BriefField,
  useAdminToken, studioFetch,
} from "../components/studio/_shared";
import ContentPlannerLayout from "../components/layout/ContentPlannerLayout";
import { MicField } from "../components/studio/MicField";

const CATEGORIES = [
  { value: "compliance",  label: "Compliance & regulation" },
  { value: "treatment",   label: "Treatment technology" },
  { value: "pumps",       label: "Pumps & pressure" },
  { value: "wastewater",  label: "Wastewater & reuse" },
  { value: "industry",    label: "Industry insights" },
  { value: "climate",     label: "Climate & trends" },
];

const SECTION_KINDS = [
  { value: "paragraph",  label: "Paragraph" },
  { value: "checklist",  label: "Checklist (tick boxes)" },
  { value: "steps",      label: "Numbered steps" },
  { value: "table",      label: "Data table" },
  { value: "callout",    label: "Callout / highlight box" },
  { value: "references", label: "References" },
];

const AUDIENCES = [
  "Lodge GMs, Municipal Engineers, Procurement Officers",
  "Lodge GMs",
  "Municipal Engineers",
  "Procurement Officers",
  "Estate trustees & managers",
  "General — water-curious public",
];

export default function DocumentStudio() {
  const navigate = useNavigate();
  const token = useAdminToken();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [creatingBrief, setCreatingBrief] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login", { replace: true });
    }
  }, [token, navigate]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const d = await studioFetch("/api/content-studio/documents", { token });
      setDocs(d.documents || []);
      if (!selectedId && d.documents && d.documents.length > 0) {
        setSelectedId(d.documents[0].id);
      }
    } catch (e) {
      if (/401|403/.test(e.message)) {
        localStorage.removeItem("hv_admin_token");
        navigate("/admin/login", { replace: true });
        return;
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, selectedId, navigate]);

  useEffect(() => { load(); }, [load]);

  const selected = docs.find((d) => d.id === selectedId);

  const draftNew = async (brief) => {
    const d = await studioFetch("/api/content-studio/documents/draft", {
      method: "POST", body: brief, token,
    });
    await load();
    setSelectedId(d.id);
    setCreatingBrief(false);
  };

  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document? This cannot be undone.")) return;
    await studioFetch(`/api/content-studio/documents/${id}`, { method: "DELETE", token });
    if (selectedId === id) setSelectedId(null);
    await load();
  };

  return (
    <ContentPlannerLayout>
      <Helmet>
        <title>Document Studio — HidroVerse</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div data-testid="document-studio">

        {error && (
          <div className="mb-4 px-4 py-3"
               style={{ background: "rgba(184,92,56,0.10)",
                        border: "1px solid rgba(184,92,56,0.35)",
                        color: "#B85C38", fontSize: "0.85rem",
                        borderRadius: 10 }}
               data-testid="doc-studio-error">
            {error}
          </div>
        )}

        <div className="grid gap-5"
             style={{ gridTemplateColumns: "minmax(0, 280px) minmax(0, 1fr)" }}>
          <aside className="space-y-3">
            <button type="button"
                    onClick={() => setCreatingBrief(true)}
                    data-testid="doc-new"
                    className="hv-btn w-full justify-center">
              <Plus size={13} /> New document
            </button>
            {loading && (
              <div className="font-mono uppercase"
                   style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                            color: "rgba(12,42,64,0.55)" }}>
                Loading…
              </div>
            )}
            {!loading && docs.length === 0 && (
              <div className="hv-card-soft" style={{ padding: 14 }}>
                <div className="font-light text-center"
                     style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.65)" }}>
                  No documents yet — click <b>New document</b>.
                </div>
              </div>
            )}
            <ul className="space-y-2" data-testid="doc-list">
              {docs.map((d) => (
                <li key={d.id}>
                  <button type="button"
                          onClick={() => setSelectedId(d.id)}
                          data-testid={`doc-item-${d.id}`}
                          className="w-full text-left p-3"
                          style={{
                            background: selectedId === d.id ? "rgba(31,182,193,0.12)" : "#FFFFFF",
                            border: `1px solid ${selectedId === d.id ? "rgba(31,182,193,0.45)" : "rgba(12,42,64,0.10)"}`,
                            borderRadius: 10, cursor: "pointer",
                          }}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <FileText size={12} style={{ color: "#0E7A82" }} />
                      <StatusBadge status={d.status} />
                    </div>
                    <div style={{ fontSize: "0.88rem", fontWeight: 600,
                                  lineHeight: 1.3 }}>
                      {d.title}
                    </div>
                    <div className="font-mono mt-1"
                         style={{ fontSize: "0.6rem",
                                  color: "rgba(12,42,64,0.55)" }}>
                      {d.page_count ? `${d.page_count}p · ` : ""}
                      {(d.tags || []).slice(0, 3).join(" · ")}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          <section>
            {creatingBrief && (
              <DocBriefForm
                onCancel={() => setCreatingBrief(false)}
                onSubmit={draftNew}
                apiToken={token}
              />
            )}
            {!creatingBrief && !selected && (
              <div className="hv-card-soft p-8 text-center"
                   data-testid="doc-empty">
                <Sparkles size={20} style={{ color: "#1FB6C1", margin: "0 auto" }} />
                <h3 className="mt-3" style={{ fontSize: "1rem", fontWeight: 600 }}>
                  No document selected
                </h3>
                <p className="mt-2 font-light max-w-md mx-auto"
                   style={{ fontSize: "0.88rem",
                            color: "rgba(12,42,64,0.65)", lineHeight: 1.55 }}>
                  Pick a document, or click <b>New document</b> to brief
                  the AI on what you want (checklist, spec sheet, one-pager).
                </p>
              </div>
            )}
            {!creatingBrief && selected && (
              <DocEditor doc={selected} token={token}
                         onUpdated={load}
                         onDeleted={() => removeDoc(selected.id)} />
            )}
          </section>
        </div>
      </div>
    </ContentPlannerLayout>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* Brief form                                                    */
/* ───────────────────────────────────────────────────────────── */
function DocBriefForm({ onCancel, onSubmit, apiToken }) {
  const [title, setTitle] = useState("");
  const [purpose, setPurpose] = useState("");
  const [outline, setOutline] = useState("");
  const [audience, setAudience] = useState(AUDIENCES[0]);
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!purpose.trim()) { setErr("Tell the AI what the document is for."); return; }
    setBusy(true); setErr("");
    try {
      await onSubmit({
        title: title.trim() || "",
        purpose: purpose.trim(),
        audience,
        outline: outline.trim() || undefined,
        tags: tags.split(/\s*,\s*/).filter(Boolean),
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hv-card-soft" style={{ padding: 20 }}
         data-testid="doc-brief-form">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono uppercase"
               style={{ fontSize: "0.62rem", letterSpacing: "0.22em",
                        color: "#0E7A82", fontWeight: 700 }}>
            Brief the AI
          </div>
          <h2 className="mt-1" style={{ fontSize: "1.15rem", fontWeight: 600 }}>
            What PDF do you want?
          </h2>
        </div>
        <button type="button" onClick={onCancel}
                disabled={busy}
                data-testid="doc-brief-cancel"
                className="hv-pill">
          Cancel
        </button>
      </div>

      <BriefField label="Title (or leave blank — AI suggests one)">
        <MicField value={title} onChange={setTitle} apiToken={apiToken}
                  testid="doc-brief-title-mic">
          <input type="text" value={title}
                 onChange={(e) => setTitle(e.target.value)}
                 data-testid="doc-brief-title-input"
                 className="hv-input w-full" />
        </MicField>
      </BriefField>

      <BriefField label="What is this document?"
                  hint="1–3 sentences. Type (checklist / spec sheet / decision tree / one-pager), purpose, and the audience pain it addresses.">
        <MicField value={purpose} onChange={setPurpose} apiToken={apiToken}
                  testid="doc-brief-purpose-mic">
          <textarea value={purpose}
                    onChange={(e) => setPurpose(e.target.value)}
                    rows={4}
                    data-testid="doc-brief-purpose-input"
                    className="hv-input w-full"
                    style={{ resize: "vertical" }}
                    placeholder="e.g. 'A pocket-format daily test card for pool/spa operators — free chlorine, pH, alkalinity, calcium hardness, cyanuric acid, combined chlorine. With target range per line and the action to take when outside.'" />
        </MicField>
      </BriefField>

      <BriefField label="Section outline (optional)"
                  hint="Bullet list, one section per line. Leave blank and the AI will decide the structure.">
        <MicField value={outline} onChange={setOutline} apiToken={apiToken}
                  testid="doc-brief-outline-mic">
          <textarea value={outline}
                    onChange={(e) => setOutline(e.target.value)}
                    rows={4}
                    data-testid="doc-brief-outline-input"
                    className="hv-input w-full"
                    style={{ resize: "vertical", fontSize: "0.88rem" }}
                    placeholder={"e.g.\n- Daily test parameters (table)\n- Action thresholds (callout)\n- Sampling procedure (steps)\n- References"} />
        </MicField>
      </BriefField>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BriefField label="Audience">
          <select value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  data-testid="doc-brief-audience-input"
                  className="hv-input w-full">
            {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </BriefField>
        <BriefField label="Tags (comma-separated)">
          <input type="text" value={tags}
                 onChange={(e) => setTags(e.target.value)}
                 data-testid="doc-brief-tags-input"
                 className="hv-input w-full"
                 placeholder="e.g. Pool & Spa, Chemistry, Operations" />
        </BriefField>
      </div>

      {err && (
        <div className="my-2 font-mono"
             style={{ fontSize: "0.78rem", color: "#B85C38" }}
             data-testid="doc-brief-error">
          {err}
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button type="button" onClick={onCancel}
                disabled={busy} className="hv-pill">
          Cancel
        </button>
        <button type="button" onClick={submit}
                disabled={busy || !purpose.trim()}
                data-testid="doc-brief-submit"
                className="hv-pill is-active">
          <Sparkles size={11} /> {busy ? "Drafting + rendering PDF (20–40s)…" : "Draft PDF"}
        </button>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* Editor                                                        */
/* ───────────────────────────────────────────────────────────── */
function DocEditor({ doc, token, onUpdated, onDeleted }) {
  const [title, setTitle] = useState(doc.title);
  const [subtitle, setSubtitle] = useState(doc.subtitle || "");
  const [summary, setSummary] = useState(doc.summary || "");
  const [kicker, setKicker] = useState(doc.kicker || "");
  const [category, setCategory] = useState(doc.category || "industry");
  const [tags, setTags] = useState((doc.tags || []).join(", "));
  const [sections, setSections] = useState(doc.sections || []);
  const [footerNote, setFooterNote] = useState(doc.footer_note || "");
  const [cover, setCover] = useState(doc.cover || "");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    setTitle(doc.title);
    setSubtitle(doc.subtitle || "");
    setSummary(doc.summary || "");
    setKicker(doc.kicker || "");
    setCategory(doc.category || "industry");
    setTags((doc.tags || []).join(", "));
    setSections(doc.sections || []);
    setFooterNote(doc.footer_note || "");
    setCover(doc.cover || "");
  }, [doc.id, doc.updated_at]);  // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (note) => {
    setBusy("save"); setErr("");
    try {
      await studioFetch(`/api/content-studio/documents/${doc.id}`, {
        method: "PATCH", token,
        body: {
          title, subtitle, summary, kicker, category, cover,
          tags: tags.split(/\s*,\s*/).filter(Boolean),
          sections, footer_note: footerNote,
          revision_note: note || "Manual edit",
        },
      });
      setSavedAt(Date.now());
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const renderPdf = async () => {
    setBusy("render"); setErr("");
    try {
      await save("Save before render");
      const r = await studioFetch(
        `/api/content-studio/documents/${doc.id}/render`,
        { method: "POST", token },
      );
      onUpdated();
      // open in a new tab so the editor can see the freshly-rendered PDF
      if (r.url) window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const refine = async (instruction) => {
    setBusy("refine"); setErr("");
    try {
      await save("Auto-save before refine");
      await studioFetch(`/api/content-studio/documents/${doc.id}/refine`, {
        method: "POST", token, body: { instruction },
      });
      onUpdated();
    } catch (e) { setErr(e.message); throw e; }
    finally { setBusy(""); }
  };

  const restore = async (revId) => {
    if (!window.confirm("Restore this revision? Current state is snapshotted first.")) return;
    setBusy("restore"); setErr("");
    try {
      await studioFetch(
        `/api/content-studio/documents/${doc.id}/revisions/${revId}/restore`,
        { method: "POST", token },
      );
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const publish = async () => {
    if (!window.confirm(
      `Publish "${title}" to /insights as a downloadable PDF now?`,
    )) return;
    setBusy("publish"); setErr("");
    try {
      await save("Save before publish");
      await studioFetch(`/api/content-studio/documents/${doc.id}/publish`, {
        method: "POST", token,
      });
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  const unpublish = async () => {
    if (!window.confirm("Unpublish this document?")) return;
    setBusy("unpublish"); setErr("");
    try {
      await studioFetch(`/api/content-studio/documents/${doc.id}/unpublish`, {
        method: "POST", token,
      });
      onUpdated();
    } catch (e) { setErr(e.message); }
    finally { setBusy(""); }
  };

  /* Section ops */
  const updateSection = (idx, patch) =>
    setSections((s) => s.map((sec, i) => (i === idx ? { ...sec, ...patch } : sec)));
  const removeSection = (idx) =>
    setSections((s) => s.filter((_, i) => i !== idx));
  const moveSection = (idx, dir) =>
    setSections((s) => {
      const next = s.slice();
      const j = idx + dir;
      if (j < 0 || j >= next.length) return s;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  const addSection = (kind = "paragraph") =>
    setSections((s) => [...s, blankSection(kind)]);

  return (
    <div className="space-y-4" data-testid={`doc-editor-${doc.id}`}>
      <div className="hv-card-soft flex items-center justify-between gap-3 flex-wrap"
           style={{ padding: 14 }}>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge status={doc.status} />
          <span className="font-mono uppercase"
                style={{ fontSize: "0.62rem", letterSpacing: "0.18em",
                         color: "rgba(12,42,64,0.55)" }}>
            /insights/resource/{doc.slug} · {doc.page_count || "?"} pages
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => save()}
                  disabled={!!busy}
                  data-testid="doc-save"
                  className="hv-pill is-active">
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          {savedAt && (
            <span className="font-mono uppercase"
                  style={{ fontSize: "0.58rem", letterSpacing: "0.16em",
                           color: "rgba(12,42,64,0.55)" }}>
              saved
            </span>
          )}
          <button type="button" onClick={renderPdf}
                  disabled={!!busy}
                  data-testid="doc-render"
                  className="hv-pill">
            <RefreshCw size={11} />
            {busy === "render" ? "Rendering…" : "Render PDF"}
          </button>
          {doc.pdf_url && (
            <a href={doc.pdf_url} target="_blank" rel="noreferrer"
               className="hv-pill"
               data-testid="doc-download"
               style={{ textDecoration: "none" }}>
              <Download size={11} /> Open current PDF
            </a>
          )}
          {doc.status === "published" ? (
            <button type="button" onClick={unpublish}
                    disabled={!!busy}
                    data-testid="doc-unpublish"
                    className="hv-pill">
              <EyeOff size={11} /> Unpublish
            </button>
          ) : (
            <button type="button" onClick={publish}
                    disabled={!!busy}
                    data-testid="doc-publish"
                    className="hv-pill is-active"
                    style={{ background: "#0E7A82", color: "#FFFFFF",
                             borderColor: "#0E7A82" }}>
              <Eye size={11} /> Publish to /insights
            </button>
          )}
          <a href={`/insights?tab=resources`} target="_blank" rel="noreferrer"
             className="hv-pill"
             data-testid="doc-preview-resources"
             style={{ textDecoration: "none" }}>
            <ExternalLink size={11} /> Insights page
          </a>
          <button type="button" onClick={onDeleted}
                  disabled={!!busy}
                  data-testid="doc-delete"
                  className="hv-pill is-danger">
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      {err && (
        <div className="px-4 py-3"
             style={{ background: "rgba(184,92,56,0.10)",
                      border: "1px solid rgba(184,92,56,0.35)",
                      color: "#B85C38", fontSize: "0.85rem",
                      borderRadius: 10 }}
             data-testid="doc-editor-error">
          {err}
        </div>
      )}

      <div className="hv-card-soft" style={{ padding: 18 }}>
        <BriefField label="Title">
          <MicField value={title} onChange={setTitle} apiToken={token}
                    testid={`doc-edit-title-mic-${doc.id}`}>
            <input type="text" value={title}
                   onChange={(e) => setTitle(e.target.value)}
                   data-testid="doc-edit-title"
                   className="hv-input w-full"
                   style={{ fontSize: "1.05rem", fontWeight: 600 }} />
          </MicField>
        </BriefField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <BriefField label="Subtitle (optional)">
            <input type="text" value={subtitle}
                   onChange={(e) => setSubtitle(e.target.value)}
                   data-testid="doc-edit-subtitle"
                   className="hv-input w-full" />
          </BriefField>
          <BriefField label="Kicker (top of cover)">
            <input type="text" value={kicker}
                   onChange={(e) => setKicker(e.target.value)}
                   data-testid="doc-edit-kicker"
                   className="hv-input w-full"
                   placeholder="HidroVerse · Checklist" />
          </BriefField>
        </div>

        <BriefField label="Summary (cover paragraph)">
          <MicField value={summary} onChange={setSummary} apiToken={token}
                    testid={`doc-edit-summary-mic-${doc.id}`}>
            <textarea value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      rows={3}
                      data-testid="doc-edit-summary"
                      className="hv-input w-full"
                      style={{ resize: "vertical" }} />
          </MicField>
        </BriefField>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <BriefField label="Category">
            <select value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="doc-edit-category"
                    className="hv-input w-full">
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </BriefField>
          <BriefField label="Cover image (path or URL, optional — shown on /insights tile)">
            <input type="text" value={cover}
                   onChange={(e) => setCover(e.target.value)}
                   data-testid="doc-edit-cover"
                   className="hv-input w-full"
                   placeholder="/generated/hero-compliance.webp" />
          </BriefField>
        </div>

        <BriefField label="Tags (comma-separated)">
          <input type="text" value={tags}
                 onChange={(e) => setTags(e.target.value)}
                 data-testid="doc-edit-tags"
                 className="hv-input w-full" />
        </BriefField>
      </div>

      {/* Sections */}
      <div className="hv-card-soft" style={{ padding: 18 }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-mono uppercase"
              style={{ fontSize: "0.65rem", letterSpacing: "0.22em",
                       color: "#0E7A82", fontWeight: 700 }}>
            Sections ({sections.length})
          </h3>
          <div className="flex flex-wrap gap-1">
            {SECTION_KINDS.map((k) => (
              <button key={k.value} type="button"
                      onClick={() => addSection(k.value)}
                      data-testid={`doc-add-section-${k.value}`}
                      className="hv-pill">
                <Plus size={10} /> {k.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {sections.length === 0 && (
            <div className="font-light text-center py-4"
                 style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.55)" }}>
              No sections yet — add one with the buttons above, or use Refine
              to ask the AI to add specific sections.
            </div>
          )}
          {sections.map((sec, idx) => (
            <SectionEditor
              key={idx}
              idx={idx}
              total={sections.length}
              section={sec}
              onChange={(patch) => updateSection(idx, patch)}
              onRemove={() => removeSection(idx)}
              onMove={(dir) => moveSection(idx, dir)}
            />
          ))}
        </div>
      </div>

      <BriefField label="Footer note (printed at bottom of last page)">
        <input type="text" value={footerNote}
               onChange={(e) => setFooterNote(e.target.value)}
               data-testid="doc-edit-footer"
               className="hv-input w-full" />
      </BriefField>

      <RefineBar onRefine={refine}
                 busy={busy === "refine"}
                 apiToken={token}
                 testidPrefix="doc" />

      <RevisionDrawer revisions={doc.revisions}
                      onRestore={restore}
                      busy={!!busy}
                      testidPrefix="doc" />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
/* Section editor                                                */
/* ───────────────────────────────────────────────────────────── */
function blankSection(kind) {
  switch (kind) {
    case "checklist":
    case "steps":
      return { kind, heading: "", rows: [{ label: "", sub: "" }] };
    case "table":
      return { kind, heading: "", headers: ["Column 1", "Column 2"], rows: [["", ""]] };
    case "callout":
      return { kind, heading: "", label: "Note", content: "" };
    case "references":
      return { kind, heading: "References", items: [""] };
    case "paragraph":
    default:
      return { kind: "paragraph", heading: "", content: "" };
  }
}

function SectionEditor({ idx, total, section, onChange, onRemove, onMove }) {
  const k = section.kind || "paragraph";
  return (
    <div className="p-3"
         style={{ background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.10)",
                  borderRadius: 10 }}
         data-testid={`doc-section-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-mono uppercase"
                style={{ fontSize: "0.58rem", letterSpacing: "0.18em",
                         color: "#0E7A82", fontWeight: 700 }}>
            §{idx + 1}
          </span>
          <select value={k}
                  onChange={(e) => onChange({ ...blankSection(e.target.value), heading: section.heading || "" })}
                  data-testid={`doc-section-kind-${idx}`}
                  className="hv-input"
                  style={{ paddingTop: 4, paddingBottom: 4,
                           fontSize: "0.82rem" }}>
            {SECTION_KINDS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={() => onMove(-1)}
                  disabled={idx === 0}
                  data-testid={`doc-section-up-${idx}`}
                  className="hv-pill">
            <ArrowUp size={10} />
          </button>
          <button type="button" onClick={() => onMove(1)}
                  disabled={idx === total - 1}
                  data-testid={`doc-section-down-${idx}`}
                  className="hv-pill">
            <ArrowDown size={10} />
          </button>
          <button type="button" onClick={onRemove}
                  data-testid={`doc-section-remove-${idx}`}
                  className="hv-pill is-danger">
            <Trash2 size={10} />
          </button>
        </div>
      </div>

      <input type="text" value={section.heading || ""}
             onChange={(e) => onChange({ heading: e.target.value })}
             data-testid={`doc-section-heading-${idx}`}
             className="hv-input w-full mb-2"
             placeholder="Section heading (optional)" />

      {(k === "paragraph") && (
        <textarea value={section.content || ""}
                  onChange={(e) => onChange({ content: e.target.value })}
                  rows={4}
                  data-testid={`doc-section-content-${idx}`}
                  className="hv-input w-full"
                  style={{ resize: "vertical" }}
                  placeholder="Write the paragraph(s). Blank line = new paragraph." />
      )}

      {(k === "callout") && (
        <>
          <input type="text" value={section.label || "Note"}
                 onChange={(e) => onChange({ label: e.target.value })}
                 data-testid={`doc-section-label-${idx}`}
                 className="hv-input w-full mb-2"
                 placeholder="Callout label (e.g. 'Action required')" />
          <textarea value={section.content || ""}
                    onChange={(e) => onChange({ content: e.target.value })}
                    rows={3}
                    data-testid={`doc-section-content-${idx}`}
                    className="hv-input w-full"
                    style={{ resize: "vertical" }} />
        </>
      )}

      {(k === "checklist" || k === "steps") && (
        <ChecklistRows
          rows={section.rows || []}
          onChange={(rows) => onChange({ rows })}
          idx={idx}
        />
      )}

      {(k === "table") && (
        <TableRows
          headers={section.headers || []}
          rows={section.rows || []}
          onChange={(patch) => onChange(patch)}
          idx={idx}
        />
      )}

      {(k === "references") && (
        <ReferenceRows
          items={section.items || []}
          onChange={(items) => onChange({ items })}
          idx={idx}
        />
      )}
    </div>
  );
}

function ChecklistRows({ rows, onChange, idx }) {
  const update = (i, patch) => onChange(rows.map((r, j) => (i === j ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => i !== j));
  const add = () => onChange([...rows, { label: "", sub: "" }]);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2"
             style={{ gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr) auto" }}>
          <input type="text" value={r.label || ""}
                 onChange={(e) => update(i, { label: e.target.value })}
                 data-testid={`doc-section-${idx}-row-${i}-label`}
                 className="hv-input"
                 placeholder="Row label" />
          <input type="text" value={r.sub || ""}
                 onChange={(e) => update(i, { sub: e.target.value })}
                 data-testid={`doc-section-${idx}-row-${i}-sub`}
                 className="hv-input"
                 placeholder="Sub-text (optional)" />
          <button type="button" onClick={() => remove(i)}
                  data-testid={`doc-section-${idx}-row-${i}-remove`}
                  className="hv-pill is-danger">
            <Trash2 size={10} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
              data-testid={`doc-section-${idx}-row-add`}
              className="hv-pill">
        <Plus size={10} /> Add row
      </button>
    </div>
  );
}

function TableRows({ headers, rows, onChange, idx }) {
  const updateHeader = (i, v) =>
    onChange({ headers: headers.map((h, j) => (i === j ? v : h)) });
  const addCol = () => onChange({
    headers: [...headers, `Col ${headers.length + 1}`],
    rows: rows.map((r) => [...r, ""]),
  });
  const removeCol = (i) => onChange({
    headers: headers.filter((_, j) => i !== j),
    rows: rows.map((r) => r.filter((_, j) => i !== j)),
  });
  const updateCell = (ri, ci, v) =>
    onChange({ rows: rows.map((r, j) => j === ri ? r.map((c, k) => (k === ci ? v : c)) : r) });
  const addRow = () => onChange({ rows: [...rows, headers.map(() => "")] });
  const removeRow = (ri) => onChange({ rows: rows.filter((_, j) => ri !== j) });
  return (
    <div>
      <div className="flex gap-2 mb-2 flex-wrap">
        {headers.map((h, i) => (
          <div key={i} className="flex items-center gap-1">
            <input type="text" value={h}
                   onChange={(e) => updateHeader(i, e.target.value)}
                   data-testid={`doc-section-${idx}-header-${i}`}
                   className="hv-input"
                   style={{ minWidth: 100, fontWeight: 700,
                            fontSize: "0.78rem" }} />
            <button type="button" onClick={() => removeCol(i)}
                    data-testid={`doc-section-${idx}-header-${i}-remove`}
                    className="hv-pill is-danger" title="Remove column">
              <Trash2 size={10} />
            </button>
          </div>
        ))}
        <button type="button" onClick={addCol}
                data-testid={`doc-section-${idx}-add-column`}
                className="hv-pill">
          <Plus size={10} /> Column
        </button>
      </div>
      <div className="space-y-1">
        {rows.map((r, ri) => (
          <div key={ri} className="flex gap-2 flex-wrap items-center">
            {r.map((c, ci) => (
              <input key={ci} type="text" value={c}
                     onChange={(e) => updateCell(ri, ci, e.target.value)}
                     data-testid={`doc-section-${idx}-row-${ri}-col-${ci}`}
                     className="hv-input"
                     style={{ minWidth: 100, fontSize: "0.82rem" }} />
            ))}
            <button type="button" onClick={() => removeRow(ri)}
                    data-testid={`doc-section-${idx}-row-${ri}-remove`}
                    className="hv-pill is-danger">
              <Trash2 size={10} />
            </button>
          </div>
        ))}
        <button type="button" onClick={addRow}
                data-testid={`doc-section-${idx}-add-row`}
                className="hv-pill">
          <Plus size={10} /> Row
        </button>
      </div>
    </div>
  );
}

function ReferenceRows({ items, onChange, idx }) {
  const update = (i, v) => onChange(items.map((it, j) => (i === j ? v : it)));
  const remove = (i) => onChange(items.filter((_, j) => i !== j));
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <input type="text" value={it}
                 onChange={(e) => update(i, e.target.value)}
                 data-testid={`doc-section-${idx}-ref-${i}`}
                 className="hv-input w-full"
                 placeholder="Citation, e.g. 'SANS 241:2015 — Drinking water specification'" />
          <button type="button" onClick={() => remove(i)}
                  data-testid={`doc-section-${idx}-ref-${i}-remove`}
                  className="hv-pill is-danger">
            <Trash2 size={10} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
              data-testid={`doc-section-${idx}-ref-add`}
              className="hv-pill">
        <Plus size={10} /> Add reference
      </button>
    </div>
  );
}
